import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnDestroy,
  inject,
  computed,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, filter, distinctUntilChanged } from 'rxjs';
import {
  InputToggleComponent,
  AlertComponent,
  ButtonComponent,
  TagComponent,
  InputTextComponent,
  InputCheckboxComponent,
  DropdownComponent,
  InputAddressComponent,
  Field,
} from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { ChoiceComponent } from '../../../shared/components/choice/choice.component';
import { ModalService } from '../../../shared/services/modal.service';
import { PaymentService } from '../services/payment.service';
import { CognitoAuthService } from '../../../shared/services/cognito-auth.service';
import { PaymentMethodStateService, SavedCard } from '../services/payment-method-state.service';
import { MakePaymentStateService } from '../services/make-payment-state.service';
import { PaymentMethodDataService } from '../../../shared/services/payment-method-data.service';
import { CardLoaderComponent } from '../../../shared/components/card-loader/card-loader.component';
import { StepUpAuthService } from '../../../shared/services/step-up-auth.service';
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { globalMessages } from '../../../shared/global-message';
import { PortletService } from '../services/portlet.service';
import { ToastService } from '../../../shared/services/toast.service';
import { UserAccessService } from '../../../shared/services/user-access.service';
import { OneIncService } from './one-inc.service';
import { mapPaymentCardsFromResponse, isCardExpired } from './payment-card.util';
import { buildBillingUpdateRequest } from './billing-request.builder';
import { createPaymentMethodFields, MONTH_OPTIONS } from './payment-method-fields.config';
import {
  PROCESSING_FEE_NOTICE_MODAL,
  DELETE_PAYMENT_MODAL,
  AUTH_REQUIRED_MODAL,
} from './payment-method-modals.config';

declare var OneInc: any;

/** Which flow the credit-card disclaimer dialog was opened for. */
type DisclaimerContext = 'newPaymentMethod' | 'setDefault' | null;

@Component({
  selector: 'app-payment-method',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    InputToggleComponent,
    AlertComponent,
    ButtonComponent,
    TagComponent,
    InputTextComponent,
    InputCheckboxComponent,
    DropdownComponent,
    InputAddressComponent,
    CardLoaderComponent,
    ChoiceComponent,
    MatDialogModule,
  ],
  templateUrl: './payment-method.component.html',
  styleUrls: ['./payment-method.component.scss'],
  providers: [OneIncService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodComponent implements OnInit, OnDestroy {
  @Input() dcPolicyFailed: any = false;
  @Input() dcBillingFailed: any = false;
  @Input() showAutoEnroll: boolean = true;
  @Output() addPaymentMethodClicked = new EventEmitter<void>();
  @Output() scrollToAddPayment = new EventEmitter<void>();

  @ViewChild('ccDisclaimerDialog', { static: false }) ccDisclaimerDialog!: TemplateRef<any>;

  readonly savedCards = signal<SavedCard[]>([]);
  readonly autoEnrollInd = signal<boolean>(true);
  readonly cardExpired = signal<boolean>(false);
  cardExpiredId: string = '';
  readonly isDataLoading = signal<boolean>(true);
  readonly processingFeeEligible = signal<boolean>(false);
  customerId: string = '';
  private destroy$ = new Subject<void>();
  private readonly paymentMethodDataService = inject(PaymentMethodDataService);
  readonly userAccess = inject(UserAccessService);
  private readonly oneIncService = inject(OneIncService);
  private readonly responsive = inject(ResponsiveService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly isMobile = toSignal(this.responsive.isMobile$, { initialValue: false });
  readonly loadingPaymentDetails = signal<boolean>(false);
  readonly editMode = signal<boolean>(false);
  readonly showForm = signal<boolean>(false);
  readonly isReadyToSave = signal<boolean>(false);
  readonly isReadyToUpdate = signal<boolean>(false);
  readonly showBillingAddress = signal<boolean>(false);
  editPaymentDetails: any = null;
  private mailingAddress: string = '';
  private mailingZipCode: string = '';
  private policyHolderName: string = '';
  paymentOwner = 'O';
  updateDefaultInDC: boolean = false;

  /** Which flow opened the CC disclaimer dialog, so confirm/cancel can resume it. */
  private disclaimerContext: DisclaimerContext = null;
  /** Card awaiting the "set as default" call once the disclaimer is confirmed. */
  private pendingDefaultCard: SavedCard | null = null;

  readonly oneIncLogo = globalMessages.logoOneInc;
  /** All shared copy, exposed so the template never hardcodes user-facing text. */
  readonly globalMessages = globalMessages;

  // Fields initialized from config factory
  private readonly fields = createPaymentMethodFields();
  paymentMethodField = this.fields.paymentMethodField;
  differentBillingAddrToggle = this.fields.differentBillingAddrToggle;
  billingAddress = this.fields.billingAddress;
  policyHolderCheckboxField = this.fields.policyHolderCheckboxField;
  cardHolderNameField = this.fields.cardHolderNameField;
  accountHolderNameField = this.fields.accountHolderNameField;
  cardNumberField = this.fields.cardNumberField;
  expirationMonthField = this.fields.expirationMonthField;
  expirationYearField = this.fields.expirationYearField;
  defaultPaymentCheckboxField = this.fields.defaultPaymentCheckboxField;
  autoEnrollPayField = this.fields.autoEnrollPayField;
  saveCardField = this.fields.saveCardField;

  notEnrolledDescriptionText = globalMessages.autoPayEnrollDescription;
  notEnrolledCaptionText = globalMessages.autoPayEnrollCaption;
  paperInvoiceDisclaimer = globalMessages.autoPayPaperInvoiceDisclaimer;

  processingFeeNoticeModal = PROCESSING_FEE_NOTICE_MODAL;
  quoteId: string = '';
  processingFee: string = '';
  ccDisclaimerMessage1 = globalMessages.ccDisclaimerMessage1;
  ccDisclaimerMessage2: string = globalMessages.ccDisclaimerMessage2;

  constructor(
    private store: Store,
    private router: Router,
    private dialog: MatDialog,
    private modalService: ModalService,
    private toastService: ToastService,
    private paymentService: PaymentService,
    private authService: CognitoAuthService,
    private stepUpAuth: StepUpAuthService,
    private paymentMethodState: PaymentMethodStateService,
    private portletService: PortletService,
    private makePaymentState: MakePaymentStateService
  ) {}

  private readonly policyNumber = this.stepUpAuth.getPolicyNumber();

  private readonly data = toSignal(
    this.store.select((state: any) => state.policy?.data),
    { initialValue: null }
  );

  readonly isLoading = toSignal(
    this.store.select((state: any) => state.policy.loading),
    { initialValue: true }
  );

  private readonly policySnapshot = computed(
    () => this.data()?.[this.policyNumber]?.policySnapshot
  );
  private readonly billingSnapshot = computed(
    () => this.data()?.[this.policyNumber]?.billingSnapshot
  );

  /** Rating state derived from the store — always current, used in getProcessingFees(). */
  private readonly ratingState = computed(() => {
    const policy = this.policySnapshot();
    if (!policy) return null;
    return policy.policyInfo?.ratingState ?? '';
  });

  /** Line of business derived from the store — always current, used in getProcessingFees(). */
  private readonly lineOfBusiness = computed(() => {
    const policy = this.policySnapshot();
    if (!policy) return null;
    return policy.policyInfo?.product ?? '';
  });

  private readonly enrolledInAutoPay = computed(() => {
    const billing = this.billingSnapshot();
    if (!billing) return null;
    return billing.paymentType?.enrolledInAutoPay ?? '';
  });

  /**
   * Billing account identifiers required by the DC Update_DefaultPaymentMethod
   * call. Falls back to the previously hardcoded dev values when the billing
   * snapshot doesn't carry them.
   */
  private readonly billingAccount = computed(() => {
    const billing = this.billingSnapshot();
    if (!billing) {
      return null;
    }
    return {
      accountId: billing?.policyInfo?.accountId ?? '',
      accountPlanCode: billing?.paymentType?.planCode ?? '',
      collectionMethod: billing?.paymentType?.collectionMethod ?? '',
    };
  });

  ngOnInit(): void {
    // In make-payment flow, listen for save trigger from make-payment component
    if (!this.showAutoEnroll) {
      this.makePaymentState.triggerSave$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.oneIncService.hasProxy()) {
          this.oneIncService.save();
        }
      });
    }

    if (this.paymentMethodState.snapshot.currentProcessingFeePercent) {
      const template = globalMessages.ccDisclaimerMessage1;
      this.ccDisclaimerMessage1 = template.replace(
        '{processingFee}',
        Number(this.paymentMethodState.snapshot.currentProcessingFeePercent).toFixed(2)
      );
    }

    // If data was already fetched (e.g. on billing-activities page), restore
    // from the cache and skip the service calls entirely.
    if (this.paymentMethodState.isLoaded) {
      const cached = this.paymentMethodState.snapshot;
      this.savedCards.set(cached.savedCards);
      this.customerId = cached.customerId;
      this.processingFeeEligible.set(cached.processingFeeEligible);
      this.processingFee = cached.currentProcessingFeePercent;
      this.evaluateCardExpiry();
      this.quoteId =
        this.policySnapshot()?.policyInfo?.quoteId ||
        this.billingSnapshot()?.policyInfo?.policyId ||
        this.paymentMethodDataService.getPaymentData().quoteId ||
        '';
      this.updateAutoEnrollField(this.enrolledInAutoPay() ?? '', false);
      this.makePaymentState.setPaymentMethod(this.savedCards().find((c) => c.isDefault) ?? null);
      this.isDataLoading.set(false);
      return;
    }

    // Subscribe to payment data (quoteId + enrolledInAutoPay)
    // Use distinctUntilChanged on quoteId to prevent duplicate service calls
    this.paymentMethodDataService.paymentData$
      .pipe(
        filter((data: any) => !!data.quoteId),
        distinctUntilChanged((prev: any, curr: any) => prev.quoteId === curr.quoteId),
        takeUntil(this.destroy$)
      )
      .subscribe((data: any) => {
        this.quoteId =
          data.quoteId ||
          this.policySnapshot()?.policyInfo?.quoteId ||
          this.billingSnapshot()?.policyInfo?.policyId ||
          '';
        this.getProcessingFees(data.billingData);
        this.updateAutoEnrollField(data.enrolledInAutoPay, false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Fetches the saved payment methods list from OneInc. */
  private getSavedPaymentMethods(): void {
    this.loadingPaymentDetails.set(true);
    const request = {
      quoteid: this.quoteId,
      action: globalMessages.actionPaymentMethods,
      customerId: this.quoteId,
      customerName: this.quoteId,
    };

    this.paymentService
      .getSavedPaymentMethods(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.responseCode === globalMessages.responseCodeSuccess) {
            this.savedCards.set(mapPaymentCardsFromResponse(response));
            this.evaluateCardExpiry();
            this.paymentMethodState.patch({
              savedCards: this.savedCards(),
            });
            let defaultCard = this.savedCards().find((c) => c.isDefault) || null;
            this.makePaymentState.setPaymentMethod(defaultCard);
            if (this.updateDefaultInDC && defaultCard) {
              this.syncDefaultPaymentToBilling(defaultCard);
              this.updateDefaultInDC = false;
            }
          }
          this.loadingPaymentDetails.set(false);
        },
        error: () => {
          this.loadingPaymentDetails.set(false);
          this.toastService.add({
            type: globalMessages.toastTypeError,
            title: globalMessages.toastPaymentMethodsFetchFailedTitle,
            message: globalMessages.toastPaymentMethodsFetchFailed,
          });
        },
      });
  }

  /** Pushes the new default payment method through to the billing (DC) system. */
  private syncDefaultPaymentToBilling(card: SavedCard): void {
    const billingAcct = this.billingAccount();
    if (!billingAcct) {
      return;
    }
    this.policyHolderName = this.policySnapshot()?.policyInfo?.name ?? '';
    const billingRequest = buildBillingUpdateRequest(card, billingAcct, this.policyHolderName);
    this.portletService
      .orchestrateData('updateDefaultPaymentToDC', billingRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
        error: (err: any) => {
          console.error('Failed to update default payment method in billing:', err);
        },
      });
  }

  updateAutoEnrollField(enrolledInAutoPay: any, userInitiated: boolean = true) {
    this.autoEnrollInd.set(
      enrolledInAutoPay === 'Yes' || enrolledInAutoPay === true ? true : false
    );
    this.autoEnrollPayField.value = this.autoEnrollInd();
    if (this.userAccess.hasEditableAccess() && !this.autoEnrollInd() && !userInitiated) {
      this.autoEnrollPayField.disabled = false;
    }
    if (this.autoEnrollInd()) {
      this.getSavedPaymentMethods();
    }
  }

  private getProcessingFees(billingData?: any): void {
    const billing = billingData || this.billingSnapshot();
    if (!billing || !this.ratingState() || !this.lineOfBusiness()) {
      return;
    }
    const token = this.authService.getAccessToken();
    const currentAmount = billing?.currentStatement?.amountToBePaidTodayInclusiveOfPastDue;
    const request = {
      action: globalMessages.actionGetProcessingFee,
      paymentType: globalMessages.paymentTypeCard,
      quoteid: this.quoteId,
      amount: currentAmount && currentAmount > 0 ? currentAmount : 10, // passing dummy value as 10 when we have outstanding balance is 0.
      stateName: this.ratingState(),
      lob: this.lineOfBusiness(),
      token: token || undefined,
    };

    this.paymentService
      .getProcessingFee(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response.responseCode === globalMessages.responseCodeSuccess) {
            this.processingFeeEligible.set(response.eligible || false);
            this.processingFee = (response.percent * 100).toString();
            this.ccDisclaimerMessage1 = this.ccDisclaimerMessage1.replace(
              '{processingFee}',
              Number(this.processingFee).toFixed(2)
            );
            this.paymentMethodState.patch({ currentProcessingFeePercent: this.processingFee });
          } else {
            this.processingFeeEligible.set(false);
          }
          // Cache the fully loaded state so make-payment can reuse it.
          this.paymentMethodState.patch({ processingFeeEligible: this.processingFeeEligible() });
          this.paymentMethodState.markLoaded();
          this.isDataLoading.set(false);
        },
        error: (error: any) => {
          this.processingFeeEligible.set(false);
          this.paymentMethodState.patch({ processingFeeEligible: false });
          this.paymentMethodState.markLoaded();
          this.isDataLoading.set(false);
        },
      });
  }

  private loadPolicyHolderInfo(): void {
    const policy = this.policySnapshot();
    if (!policy) return;
    const policyInfo = policy.policyInfo;
    this.policyHolderName = policyInfo?.name ?? '';
    if (this.policyHolderCheckboxField.value) {
      const name = this.policyHolderName;
      this.cardHolderNameField.value = name;
      this.accountHolderNameField.value = name;
    }
    this.resolveMailingAddress(policy);
  }

  /**
   * The DC policy snapshot carries the insured's address as a single formatted
   * string on policyGeneralInformation — there is no nested `mailingAddress`
   * object on this payload, so reading one left the address permanently empty.
   */
  private resolveMailingAddress(policy: any): void {
    this.mailingAddress = policy?.propertyAddressInfo?.fullAddress || '';
    this.mailingZipCode = policy?.propertyAddressInfo?.zipCode || '';

    const mailingAddressDifferent = policy?.insuredInfo?.mailingAddressDifferent || 'No';
    if (mailingAddressDifferent.toLowerCase() != 'no') {
      this.mailingAddress = policy?.insuredInfo?.mailingFullAddress || '';
      this.mailingZipCode = policy?.insuredInfo?.mailingZip || '';
    }
  }

  evaluateCardExpiry(): void {
    const defaultCard = this.savedCards().find((c) => c.isDefault);
    if (defaultCard && isCardExpired(defaultCard)) {
      this.cardExpired.set(true);
      this.cardExpiredId = defaultCard.id;
    }
  }

  /**
   * Sets a saved payment method as the default. When a processing fee applies to
   * credit cards the user first acknowledges the fee notice, then the OneInc
   * disclaimer, before the call is made — mirroring the billing-details flow.
   */
  setAsDefault(card: SavedCard, userInitiated: boolean = true): void {
    this.makePaymentState.setPaymentMethod(card);
    // Skip if the already-selected radio button is clicked again
    if (card.isDefault) {
      return;
    }

    const needsFeeConsent =
      this.processingFeeEligible() &&
      card.paymentMethodType === globalMessages.paymentTypeCreditCard &&
      userInitiated;

    if (!needsFeeConsent) {
      this.updateDefaultPaymentMethod(card);
    } else {
      this.modalService
        .confirm(this.processingFeeNoticeModal)
        .pipe(takeUntil(this.destroy$))
        .subscribe((feeResponse) => {
          if (feeResponse[0] === globalMessages.modalConfirmContinue) {
            // Second gate: the One Inc authorization disclaimer.
            this.pendingDefaultCard = card;
            this.requestDisclaimerConsent('setDefault');
          }
        });
    }
  }

  updateDefaultPaymentMethod(item: SavedCard): void {
    this.loadingPaymentDetails.set(true);
    const request = {
      accountId: this.billingSnapshot()?.policyInfo?.accountId,
      paymentMethodTokenId: item.id,
    };
    this.portletService.orchestrateData('updateDefaultPayment', request).subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          this.updateDefaultInDC = true;
          this.getSavedPaymentMethods();
          this.toastService.add({
            type: globalMessages.toastTypeSuccess,
            title: '',
            message: globalMessages.toastPaymentMethodUpdated,
          });
        } else {
          this.toastService.add({
            type: 'error',
            title: 'Error',
            message: 'An unexpected error occurred. Please try again.',
          });
        }
      },
      error: () => {
        this.loadingPaymentDetails.set(false);
        this.portletService.updateAudit('FailedToUpdatedDefaultPaymentMethod');
        this.toastService.add({
          type: globalMessages.toastTypeError,
          title: globalMessages.toastPaymentMethodAddFailedTitle,
          message: globalMessages.toastPaymentMethodAddFailed,
        });
      },
    });
  }

  editCard(card: SavedCard): void {
    this.editPaymentDetails = card;
    this.showBillingAddress.set(false);
    this.differentBillingAddrToggle.value = false;
    this.billingAddress.value = '';

    // Show the address OneInc has on file so the user knows what they'd override.
    const onFileAddress = card.cardHolderAddress || this.mailingAddress;
    const zip = card.billingZipCode ?? '';
    this.differentBillingAddrToggle.caption =
      globalMessages.billingAddressDifferentFrom +
      (!zip || onFileAddress.includes(zip) ? onFileAddress : `${onFileAddress}, ${zip}`);

    this.cardHolderNameField.value = card.cardholderName;
    this.cardHolderNameField.disabled = true;
    this.cardNumberField.value = '**** **** **** ' + card.lastFour;

    // Prefer the raw OneInc parts; fall back to parsing the MM/YYYY display value.
    const [expMonthStr, expYearStr] = card.expiryDate?.split('/') ?? ['', ''];
    const expMonth = card.cardExpirationMonth ?? Number(expMonthStr);
    const expYear = card.cardExpirationYear ?? Number(expYearStr);

    // The dropdown only picks up a new selection when the Field instance changes.
    this.expirationMonthField = new Field({
      name: 'expirationMonth',
      caption: globalMessages.captionExpirationMonth,
      type: 'dropdown',
      required: true,
      id: 'expirationMonth',
      value: expMonth ? String(expMonth) : '',
      min: 1,
      max: 12,
      options: { optionsList: MONTH_OPTIONS as any[] },
    });
    this.expirationYearField.value = expYear ? String(expYear) : '';
    this.defaultPaymentCheckboxField.value = false;

    this.showForm.set(false);
    this.editMode.set(true);
    this.isReadyToSave.set(false);
    this.validateExpirationDate();
  }

  //Update Existing Credit Card information
  updateCard(): void {
    this.editMode.set(false);
    this.loadingPaymentDetails.set(true);
    let updateAsDefault = this.defaultPaymentCheckboxField.value;
    if (this.editPaymentDetails?.paymentMethodType === globalMessages.paymentTypeEft) {
      return;
    }
    if (!this.validateExpirationDate()) {
      return;
    }
    const billingZip = this.differentBillingAddrToggle.value
      ? this.billingAddress.value?.zipCode
      : this.editPaymentDetails.billingZipCode || '';

    const request = {
      quoteid: this.quoteId,
      paymentMethodTokenId: this.editPaymentDetails.id,
      expirationYear: Number(this.expirationYearField.value),
      expirationMonth: Number(this.expirationMonthField.value),
      zipCode: billingZip,
    };

    this.portletService.orchestrateData('updateCard', request).subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          if (updateAsDefault) {
            this.updateDefaultPaymentMethod(this.editPaymentDetails);
          } else {
            let defaultCard = this.savedCards().find((c) => c.isDefault) || null;
            if (defaultCard && defaultCard.id === this.editPaymentDetails.id) {
              this.updateDefaultInDC = true;
            }
            this.getSavedPaymentMethods();
          }
          this.toastService.add({
            type: globalMessages.toastTypeSuccess,
            title: '',
            message: globalMessages.toastPaymentMethodUpdated,
          });
        } else {
          this.toastService.add({
            type: 'error',
            title: 'Error',
            message: 'An unexpected error occurred. Please try again.',
          });
        }
      },
      error: () => {
        this.getSavedPaymentMethods();
        this.toastService.add({
          type: globalMessages.toastTypeError,
          title: globalMessages.toastPaymentMethodUpdateFailedTitle,
          message: globalMessages.toastPaymentMethodUpdateFailed,
        });
      },
    });
  }

  //Delete Existing Payment method
  deleteCard(card: SavedCard): void {
    this.modalService
      .confirm(DELETE_PAYMENT_MODAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe((popupCallbackDetail) => {
        if (popupCallbackDetail[0] !== globalMessages.modalConfirmDelete) {
          return;
        }
        this.loadingPaymentDetails.set(true);

        const request = {
          quoteid: this.quoteId,
          paymentMethodTokenId: card.id,
        };

        this.portletService
          .orchestrateData(globalMessages.actionDeletePaymentMethod, request)
          .subscribe({
            next: (response) => {
              if (response.statusCode === 200) {
                this.getSavedPaymentMethods();
                this.toastService.add({
                  type: globalMessages.toastTypeSuccess,
                  title: '',
                  message: globalMessages.toastPaymentMethodDeleted,
                });
              } else {
                this.loadingPaymentDetails.set(false);
              }
            },
            error: () => {
              this.loadingPaymentDetails.set(false);
              this.toastService.add({
                type: globalMessages.toastTypeError,
                title: globalMessages.toastPaymentMethodsFetchFailedTitle,
                message: globalMessages.toastPaymentMethodsFetchFailed,
              });
            },
          });
      });
  }

  addPaymentMethod(): void {
    if (this.userAccess.hasFullAccess()) {
      this.displayPaymentForm();
    } else if (this.userAccess.hasEditableAccess()) {
      this.modalService
        .confirm(AUTH_REQUIRED_MODAL)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          if (response[0] === globalMessages.modalConfirmContinue) {
            this.router.navigate([globalMessages.routeOtp]);
          }
        });
    }
  }

  hidePaymentForm(): void {
    this.editMode.set(false);
    this.showForm.set(false);
    this.isReadyToSave.set(false);
    this.isReadyToUpdate.set(false);
    this.showBillingAddress.set(false);
    this.differentBillingAddrToggle.value = false;
    this.billingAddress.value = '';
    this.policyHolderCheckboxField.value = false;
    this.cardHolderNameField.value = '';
    this.accountHolderNameField.value = '';
    this.paymentMethodField.value = '';
    this.saveCardField.value = '';
    this.oneIncService.hideModal();
    if (!this.showAutoEnroll) {
      this.makePaymentState.setPaymentMethod(null);
    }
  }

  //Display payment form when user clicks 'Add Payment method'
  displayPaymentForm() {
    this.showForm.set(true);
    this.isReadyToSave.set(false);
  }

  sessionID: any;
  portalOne: any;

  //Method called when payment method changes
  paymentMethodChange(paymentMethod: any, event: any) {
    //this.sessionID = null;
    //this.portalOne = null;
    this.isReadyToSave.set(false);
    let ele = document.getElementById('portalOneContainer');
    while (ele?.firstChild) {
      ele.removeChild(ele.firstChild);
    }

    const popupCreditCard = {
      type: 'actionDialog',
      imageSrc: '../../../assets/images/exclamation-octagon-orange.svg',
      message: globalMessages.modalProcessingFeeTitle,
      messageExt: globalMessages.modalProcessingFeeBody,
      confirm: 'Continue',
      cancel: 'Cancel',
      fields: [],
    };

    if (event) {
      if (this.processingFeeEligible()) {
        if (paymentMethod == 'EFTC') {
          this.modalService.confirm(popupCreditCard).subscribe((popupCallbackDetail) => {
            if (popupCallbackDetail[0] === 'Continue') {
              //this.viewProcessingFees = true;
              this.paymentMethodField.value = paymentMethod;
              this.differentBillingAddrToggle.value = false;
              this.billingAddress.value = '';
              this.policyHolderCheckboxField.value = true;
              this.toggleName(true);
            } else {
              //this.viewProcessingFees = false;
              this.paymentMethodField.value = '';
              document.getElementById('EFTC')!.click();
            }
          });
        } else {
          //this.viewProcessingFees = false;
          this.paymentMethodField.value = paymentMethod;
          this.differentBillingAddrToggle.value = false;
          this.billingAddress.value = '';
          this.policyHolderCheckboxField.value = true;
          this.toggleName(true);
        }
      }
      if (!this.processingFeeEligible()) {
        //this.viewProcessingFees = false;
        this.paymentMethodField.value = paymentMethod;
        this.differentBillingAddrToggle.value = false;
        this.billingAddress.value = '';
        this.policyHolderCheckboxField.value = true;
        this.toggleName(true);
      }
    } else {
      //this.viewProcessingFees = false;
      this.paymentMethodField.value = '';
    }
  }

  // displayPaymentForm(): void {
  //   this.showForm = true;
  //   this.editMode = false;
  //   this.isReadyToSave = false;
  //   this.saveCardField.value = '';
  //   this.paymentMethodField.value = '';
  //   this.getCustomerId();
  // }

  displayPaymentMethod(value: string): void {
    this.saveCardField.value = value;
    this.paymentMethodField.value = '';
    this.oneIncService.hideModal();
  }

  //Method will be called when CC or EFT is selected
  selectPaymentMethod(value: string, event: any): void {
    this.oneIncService.hideModal();
    if (event) {
      if (this.processingFeeEligible() && value === globalMessages.collectionMethodEFTC) {
        // Show processing fee notice before displaying additional form options
        this.modalService
          .confirm(this.processingFeeNoticeModal)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            if (res[0] === globalMessages.modalConfirmContinue) {
              this.loadPolicyHolderInfo();
              this.paymentMethodField.value = value;
              this.policyHolderCheckboxField.value = true;
              this.toggleName(true);
            } else {
              // Cancel: clear credit card selection and reset form state
              this.oneIncService.hideModal();
              this.paymentMethodField.value = '';
              this.policyHolderCheckboxField.value = false;
              this.cardHolderNameField.value = '';
              this.accountHolderNameField.value = '';
            }
            // Selection resolved asynchronously; refresh the choice chip highlight.
            this.cdr.markForCheck();
          });
      } else {
        this.loadPolicyHolderInfo();
        this.paymentMethodField.value = value;
        this.policyHolderCheckboxField.value = true;
        this.toggleName(true);
      }
    } else {
      this.paymentMethodField.value = '';
    }
    // paymentMethodField.value is a mutated object property that several
    // [selected] bindings depend on. Under OnPush, mark for check so the
    // choice chips re-evaluate their highlight on the first selection.
    this.cdr.markForCheck();
  }

  toggleName(value: boolean): void {
    this.policyHolderCheckboxField.value = value;
    const name = value ? this.policyHolderName : '';
    this.cardHolderNameField.value = name;
    this.accountHolderNameField.value = name;
    this.cardHolderNameField.disabled = value;
    this.accountHolderNameField.disabled = value;
    this.openPortalOneModal();
  }

  changeAlternateBillingAddress(value: boolean): void {
    this.differentBillingAddrToggle.value = value;
    this.showBillingAddress.set(value);
    this.billingAddress.value = value ? this.billingAddress.value : '';
    if (!this.editMode()) {
      this.openPortalOneModal();
    }
  }

  onBillingAddressChanged(): void {
    if (this.billingAddress.value?.formattedAddress || this.billingAddress.value?.zipCode) {
      if (!this.editMode()) {
        this.openPortalOneModal();
      }
    }
  }

  openPortalOneModal(): void {
    const billingAddrInd = this.differentBillingAddrToggle.value || false;
    const billingZip =
      billingAddrInd && this.billingAddress.value?.zipCode ? this.billingAddress.value.zipCode : '';
    const policyHolderInd = this.policyHolderCheckboxField.value || false;
    const cardHolderName = this.cardHolderNameField.value;
    const accountHolderHome = this.accountHolderNameField.value;

    if (this.autoEnrollInd()) {
      this.getCustomerId(this.quoteId);
    } else {
      this.getCustomerId(this.policyNumber);
    }

    const paymentMethod = this.paymentMethodField.value;
    const billingAddressFilled = !billingAddrInd || !!billingZip;
    const notReady =
      (paymentMethod === 'EFTC' && !policyHolderInd && !cardHolderName) ||
      (paymentMethod === 'EFT' &&
        (!billingAddressFilled || (!policyHolderInd && !accountHolderHome)));

    if (notReady) {
      this.sessionID = null;
      this.portalOne = null;
      this.oneIncService.hideModal();
      this.isReadyToSave.set(false);
      if (!this.showAutoEnroll) {
        this.makePaymentState.setPaymentMethod(null);
      }
      let ele = document.getElementById('portalOneContainer');
      while (ele?.firstChild) {
        ele.removeChild(ele.firstChild);
      }
    } else {
      this.openSavePayment();
    }
  }

  //Get Customer Id based on user is saving the card
  private getCustomerId(data: string): void {
    const token = this.authService.getAccessToken();
    const request = {
      quoteid: data,
      action: globalMessages.actionAccount,
      customerId: data,
      customerName: data,
      token: token || undefined,
    };

    this.paymentService
      .getCustomerId(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response.customerId) {
            this.customerId = response.customerId;
            this.paymentMethodState.patch({ customerId: this.customerId });
          }
        },
        error: (error: any) => {
          console.error('Failed to get customer ID:', error);
        },
      });
  }

  private openSavePayment(): void {
    this.oneIncService.hideModal();
    this.isReadyToSave.set(false);

    this.oneIncService.openSession(
      this.quoteId,
      this.destroy$,
      (sessionKey: string) => {
        this.openOneIncWindow(sessionKey);
      },
      () => {
        this.isReadyToSave.set(false);
        this.toastService.add({
          type: globalMessages.toastTypeError,
          title: globalMessages.toastFailedTitle,
          message: globalMessages.toastPaymentSessionFailed,
        });
      }
    );
  }

  private openOneIncWindow(sessionKey: string): void {
    let billingAddr = this.mailingAddress;
    let billingZip = this.mailingZipCode;
    if (this.billingAddress.value?.formattedAddress || this.billingAddress.value?.zipCode) {
      billingAddr = this.billingAddress.value?.formattedAddress || '';
      billingZip = this.billingAddress.value?.zipCode || '';
    }
    const isCreditCard = this.paymentMethodField.value === globalMessages.collectionMethodEFTC;
    const holderName = isCreditCard
      ? this.cardHolderNameField.value
      : this.accountHolderNameField.value;

    if (this.policyHolderCheckboxField.value) {
      this.paymentOwner = 'I';
    }

    this.oneIncService.openWindow(
      globalMessages.oneIncContainerId,
      {
        quoteId: this.quoteId,
        customerId: this.customerId,
        sessionKey,
        paymentCategory: isCreditCard
          ? OneInc.PaymentCategory.CreditCard
          : OneInc.PaymentCategory.ECheck,
        billingZip,
        billingAddressStreet: billingAddr,
        policyHolderName: holderName,
      },
      {
        onSuccess: (result: any) => {
          this.loadingPaymentDetails.set(true);
          const isFirstCard = this.savedCards().length === 0;
          this.hidePaymentForm();
          let request = { paymentMethodTokenId: result.tokenId };
          this.portletService.updateAudit('PaymentMethodAdded', request);
          if (!this.showAutoEnroll) {
            // Make-payment flow: notify with tokenId so charge can proceed
            this.makePaymentState.setPaymentMethod({
              id: result.tokenId,
              type: 'visa',
              lastFour: '',
              cardholderName: '',
              expiryDate: '',
              isDefault: false,
              logoSrc: '',
              paymentMethodType:
                result.paymentCategory === 'CreditCard'
                  ? globalMessages.paymentTypeCreditCard
                  : globalMessages.paymentTypeEft,
            } as SavedCard);
            this.makePaymentState.notifySaveComplete(result.tokenId);
          } else if (this.autoEnrollInd()) {
            this.toastService.add({
              type: globalMessages.toastTypeSuccess,
              title: '',
              message: globalMessages.toastPaymentMethodUpdated,
            });
            if (isFirstCard) {
              this.setAsDefault(
                { id: result.tokenId, paymentMethodType: result.paymentCategory } as SavedCard,
                false
              );
            } else {
              this.getSavedPaymentMethods();
            }
          }
        },
        onFail: () => {
          this.isReadyToSave.set(false);
          this.toastService.add({
            type: globalMessages.toastTypeError,
            title: globalMessages.toastPaymentMethodAddFailedTitle,
            message: globalMessages.toastPaymentMethodAddFailed,
          });
        },
        onValidationComplete: (isReadyToSave: boolean) => {
          this.isReadyToSave.set(isReadyToSave);
          // In make-payment flow (showAutoEnroll=false), signal the state service
          // so the Submit Payment button in the navigation footer gets enabled.
          if (!this.showAutoEnroll) {
            if (isReadyToSave) {
              const isCreditCard =
                this.paymentMethodField.value === globalMessages.collectionMethodEFTC;
              this.makePaymentState.setPaymentMethod({
                id: 'pending-oneinc',
                type: 'visa',
                lastFour: '',
                cardholderName: isCreditCard
                  ? this.cardHolderNameField.value
                  : this.accountHolderNameField.value,
                expiryDate: '',
                isDefault: false,
                logoSrc: '',
                paymentMethodType: isCreditCard
                  ? globalMessages.paymentTypeCreditCard
                  : globalMessages.paymentTypeEft,
              } as SavedCard);
            } else {
              this.makePaymentState.setPaymentMethod(null);
            }
          }
        },
      }
    );
  }

  saveNewPaymentMethod(): void {
    if (!this.oneIncService.hasProxy()) {
      return;
    }
    const isCreditCard = this.paymentMethodField.value === globalMessages.collectionMethodEFTC;
    // When saveCard = 'No', skip saving the token — just trigger the charge directly via onSuccess.
    if (this.saveCardField.value === 'No') {
      this.oneIncService.save();
      return;
    }
    if (!isCreditCard) {
      this.oneIncService.save();
      return;
    }
    if (this.processingFeeEligible()) {
      this.modalService
        .confirm(this.processingFeeNoticeModal)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res[0] === globalMessages.modalConfirmContinue) {
            this.requestDisclaimerConsent('newPaymentMethod');
          }
        });
    } else {
      this.requestDisclaimerConsent('newPaymentMethod');
    }
  }

  /* --------------------------- CC disclaimer dialog -------------------------- */

  /** Opens the One Inc authorization disclaimer and remembers what to resume. */
  private requestDisclaimerConsent(context: DisclaimerContext): void {
    this.disclaimerContext = context;
    this.openDisclaimerDialog();
  }

  openDisclaimerDialog(): void {
    this.dialog.open(this.ccDisclaimerDialog, {
      maxWidth: '792px',
      panelClass: 'cc-disclaimer-dialog',
      disableClose: true,
    });
  }

  confirmDisclaimer(): void {
    const context = this.disclaimerContext;
    this.disclaimerContext = null;
    this.dialog.closeAll();

    if (context === 'setDefault') {
      const card = this.pendingDefaultCard;
      this.pendingDefaultCard = null;
      if (card) {
        this.updateDefaultPaymentMethod(card);
      }
      return;
    }
    if (context === 'newPaymentMethod') {
      // Hands the card off to One Inc; the outcome arrives via onSuccess / onFail.
      this.oneIncService.save();
    }
  }

  cancelDisclaimer(): void {
    this.disclaimerContext = null;
    this.pendingDefaultCard = null;
    this.dialog.closeAll();
  }

  validateExpirationDate(): boolean {
    this.isReadyToUpdate.set(false);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const expYear = Number(this.expirationYearField.value);
    const expMonth = Number(this.expirationMonthField.value);
    if (!expYear || !expMonth) return false;
    if (currentYear > expYear) return false;
    if (currentYear === expYear && currentMonth > expMonth) return false;
    this.isReadyToUpdate.set(true);
    return true;
  }
}
